import React, { useState, useEffect, useRef } from "react";
import DashboardCard from "./DashboardCard";
import ContentCard from "./ContentCard";
import { dashboardItems, maintenanceItems } from "./dashboardData";
import { useNotification } from "../../context/NotificationContext";
import NotificationSnackbar from "../../utils/NotificationSnackbar";
import useAuth from "../../hooks/useAuth";

const ControlPanel = () => {
  const { user } = useAuth();
  const [selectedItem, setSelectedItem] = useState(null);
  const { notification, closeNotification } = useNotification();
  const leftColumnRef = useRef(null);
  const isAdminOrSuperAdmin = user?.role === 'Admin' || user?.role === 'Super Admin';
  
  const filteredDashboardItems = dashboardItems.filter(item => {
    if (item.id === 'user-management' && !isAdminOrSuperAdmin) {
      return false;
    }
    return true;
  });
  
  useEffect(() => {
    if (filteredDashboardItems.length > 0 && !selectedItem) {
      setSelectedItem(filteredDashboardItems[0]);
    }
  }, [filteredDashboardItems]);
  
  useEffect(() => {
    const updateHeight = () => {
      // Only apply height sync in desktop view (md and above)
      if (window.innerWidth >= 768 && leftColumnRef.current) {
        const leftColumnHeight = leftColumnRef.current.offsetHeight;
        const contentColumn = document.getElementById('content-column');
        if (contentColumn) {
          contentColumn.style.height = `${leftColumnHeight}px`;
        }
      }
    };
    
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);
  
  const handleItemClick = (item) => {
    setSelectedItem(item);
  };
  
  return (
    <div className="bg-white p-4 mt-24 md:mt-0 md:p-6">
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
      
      {/* Desktop View: Three-column grid */}
      <div className="hidden md:grid md:grid-cols-12 gap-2 relative z-0">
        {/* Left Column: Operation and Control */}
        <div 
          ref={leftColumnRef} 
          className="md:col-span-3 space-y-4 min-h-full z-10"
        >
          <h2 className="text-md md:text-lg font-semibold text-blue-800 mb-2">
            OPERATION AND CONTROL
          </h2>
          {filteredDashboardItems.map((item) => (
            <DashboardCard
              key={item.id}
              Icon={item.icon}
              title={item.title}
              description={item.description}
              onClick={() => handleItemClick(item)}
              isMaintenanceCard={false}
              isSelected={selectedItem?.id === item.id}
              disabled={item.id === 'user-management' && !isAdminOrSuperAdmin}
            />
          ))}
        </div>
        
        {/* Middle Column: Content */}
        <div id="content-column" className="md:col-span-6 z-20">
          <ContentCard content={selectedItem} isMobile={false} />
        </div>
        
        {/* Right Column: Maintenance */}
        <div className="md:col-span-3 space-y-4 z-10">
          <h2 className="text-md md:text-lg font-semibold text-blue-800 mb-2">
            MAINTENANCE
          </h2>
          {maintenanceItems.map((item) => (
            <DashboardCard
              key={item.id}
              Icon={item.icon}
              title={item.title}
              description={item.description}
              onClick={() => handleItemClick(item)}
              isMaintenanceCard={true}
              isSelected={selectedItem?.id === item.id}
            />
          ))}
        </div>
      </div>
      
      {/* Mobile View: Accordion-like layout */}
      <div className="md:hidden grid grid-cols-1 gap-2">
        {/* Operation and Control */}
        <h2 className="text-md font-semibold text-blue-800 mb-2">
          OPERATION AND CONTROL
        </h2>
        {filteredDashboardItems.map((item) => (
          <div key={item.id}>
            <DashboardCard
              Icon={item.icon}
              title={item.title}
              description={item.description}
              onClick={() => handleItemClick(item)}
              isMaintenanceCard={false}
              isSelected={selectedItem?.id === item.id}
              disabled={item.id === 'user-management' && !isAdminOrSuperAdmin}
            />
            {selectedItem?.id === item.id && (
              <ContentCard content={selectedItem} isMobile={true} />
            )}
          </div>
        ))}
        
        {/* Maintenance */}
        <h2 className="text-md font-semibold text-blue-800 mb-2 mt-4">
          MAINTENANCE
        </h2>
        {maintenanceItems.map((item) => (
          <div key={item.id}>
            <DashboardCard
              Icon={item.icon}
              title={item.title}
              description={item.description}
              onClick={() => handleItemClick(item)}
              isMaintenanceCard={true}
              isSelected={selectedItem?.id === item.id}
            />
            {selectedItem?.id === item.id && (
              <ContentCard content={selectedItem} isMobile={true} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ControlPanel;